if (!customElements.get('popup-modal')) {
  customElements.define(
    'popup-modal',
    class PopupModal extends HTMLElement {
      constructor() {
        super();

        this.overlay = this.querySelector('.popup-modal__overlay');
        this.closeBtn = this.querySelector('.popup-modal__close');
        this.dialog = this.querySelector('.popup-modal__dialog');

        this.closeBtn?.addEventListener('click', () => this.hide());
        this.overlay?.addEventListener('click', () => this.hide());
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });

        // Email gate form handling
        if (this.dataset.requireEmail === 'true') {
          this.form = this.querySelector('.popup-modal__form');
          this.emailStep = this.querySelector('[data-step="email"]');
          this.downloadStep = this.querySelector('[data-step="download"]');
          this.statusEl = this.querySelector('.popup-modal__form-status');

          if (this.form) {
            this.form.addEventListener('submit', (e) => this.handleEmailSubmit(e));
          }
        }
      }

      connectedCallback() {
        if (this.moved) return;
        this.moved = true;

        // Store section ID for localStorage key
        const section = this.closest('.shopify-section');
        this.sectionId = section ? section.id.replace('shopify-section-', '') : this.id;

        // Move to body to ensure proper z-index stacking
        document.body.appendChild(this);

        // If user already submitted email, go straight to download step
        if (this.dataset.requireEmail === 'true' && this.hasSubmittedEmail()) {
          this.showDownloadStep();
        }

        // Check if should show
        if (!this.isDismissed()) {
          const delay = parseInt(this.dataset.delay || '0', 10) * 1000;
          setTimeout(() => this.show(), delay);
        }
      }

      get storageKey() {
        return `popup-modal-dismissed-${this.sectionId}`;
      }

      get emailStorageKey() {
        return `popup-modal-email-submitted-${this.sectionId}`;
      }

      isDismissed() {
        try {
          const dismissedData = localStorage.getItem(this.storageKey);
          if (!dismissedData) return false;

          const { timestamp, daysHidden } = JSON.parse(dismissedData);
          const dismissedDate = new Date(timestamp);
          const now = new Date();
          const daysSinceDismissed = (now - dismissedDate) / (1000 * 60 * 60 * 24);

          return daysSinceDismissed < daysHidden;
        } catch (e) {
          return false;
        }
      }

      setDismissed() {
        try {
          const daysHidden = parseInt(this.dataset.daysHidden || '7', 10);
          localStorage.setItem(
            this.storageKey,
            JSON.stringify({
              timestamp: new Date().toISOString(),
              daysHidden: daysHidden,
            })
          );
        } catch (e) {
          // localStorage not available
        }
      }

      hasSubmittedEmail() {
        try {
          return localStorage.getItem(this.emailStorageKey) === 'true';
        } catch (e) {
          return false;
        }
      }

      setEmailSubmitted() {
        try {
          localStorage.setItem(this.emailStorageKey, 'true');
        } catch (e) {
          // localStorage not available
        }
      }

      showDownloadStep() {
        if (this.emailStep) this.emailStep.hidden = true;
        if (this.downloadStep) this.downloadStep.hidden = false;
      }

      async handleEmailSubmit(e) {
        e.preventDefault();

        const emailInput = this.form.querySelector('input[type="email"]');
        const submitBtn = this.form.querySelector('.popup-modal__submit');
        const email = emailInput?.value?.trim();

        if (!email || !emailInput.validity.valid) {
          this.showStatus('Please enter a valid email address.', 'error');
          emailInput?.focus();
          return;
        }

        // Disable form while submitting
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';

        try {
          // Convert standard Mailchimp POST URL to JSONP-compatible URL
          const formAction = this.form.getAttribute('action');
          const jsonpUrl = formAction.replace('/post?', '/post-json?') + '&EMAIL=' + encodeURIComponent(email) + '&c=?';

          // Use JSONP for cross-origin Mailchimp submission
          const result = await this.mailchimpJsonp(jsonpUrl);

          if (result.result === 'success' || (result.result === 'error' && result.msg && result.msg.includes('already subscribed'))) {
            this.setEmailSubmitted();
            this.showDownloadStep();
          } else {
            this.showStatus(result.msg || 'Something went wrong. Please try again.', 'error');
            submitBtn.disabled = false;
            submitBtn.textContent = this.form.querySelector('.popup-modal__submit')?.dataset?.originalText || 'Get the Guide';
          }
        } catch (err) {
          // On network error, still allow through (don't block the download)
          this.setEmailSubmitted();
          this.showDownloadStep();
        }
      }

      mailchimpJsonp(url) {
        return new Promise((resolve, reject) => {
          const callbackName = 'mc_callback_' + Math.random().toString(36).substring(2);
          const timeout = setTimeout(() => {
            cleanup();
            reject(new Error('Request timed out'));
          }, 10000);

          function cleanup() {
            clearTimeout(timeout);
            delete window[callbackName];
            if (script.parentNode) script.parentNode.removeChild(script);
          }

          window[callbackName] = (data) => {
            cleanup();
            resolve(data);
          };

          const script = document.createElement('script');
          script.src = url.replace('&c=?', '&c=' + callbackName);
          script.onerror = () => {
            cleanup();
            reject(new Error('Network error'));
          };
          document.head.appendChild(script);
        });
      }

      showStatus(message, type) {
        if (!this.statusEl) return;
        // Strip HTML tags from Mailchimp responses
        const cleanMessage = message.replace(/<[^>]*>/g, '');
        this.statusEl.textContent = cleanMessage;
        this.statusEl.hidden = false;
        this.statusEl.className = 'popup-modal__form-status popup-modal__form-status--' + type;
      }

      show() {
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        trapFocus(this, this.dialog);
      }

      hide() {
        document.body.classList.remove('overflow-hidden');
        this.removeAttribute('open');
        removeTrapFocus();
        this.setDismissed();
      }
    }
  );

  const POPUP_MODAL_HASH = '#guide';

  function openPopupModalFromHash() {
    const modal = document.querySelector('popup-modal');
    if (!modal) return false;
    modal.show();
    return true;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    if (href === POPUP_MODAL_HASH || href.endsWith(POPUP_MODAL_HASH)) {
      if (openPopupModalFromHash()) {
        event.preventDefault();
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  });

  if (window.location.hash === POPUP_MODAL_HASH) {
    const trigger = () => {
      openPopupModalFromHash();
      history.replaceState(null, '', window.location.pathname + window.location.search);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', trigger);
    } else {
      trigger();
    }
  }
}
